import { IconEdit, IconPlusCircle } from 'assets/images'
import { Container, Loading, Table } from 'components'
import HeatmapManager from 'components/HeatmapManager'
import { ColumnProps } from 'components/Table/types'
import useUser from 'hooks/useUser'
import { t } from 'i18n'
// removed unused ButtonContainer and ColumnTitle imports
import React, { useCallback, useEffect, useState } from 'react'
import { If } from 'react-extras'
import { connect } from 'react-redux'
import { Dispatch, bindActionCreators } from 'redux'
import { getSlopesList } from 'services'
import { Seasons, Slope, UserInfo } from 'store/ducks'
import * as AuthActions from 'store/ducks/auth/actions'
import { ApplicationState } from '../../store'
import {
  AddSlopeButton,
  AddSlopeButtonLabel,
  ButtonsContainer,
  EditButton,
  InputLabel,
  PageTitle,
  RadioInputLabel
} from './styled'

interface Props {
  user: UserInfo
}

enum Key {
  NAME = 'name',
  TYPES = 'types'
}

const SlopesPage = ({ user }: Props) => {
  // const { resort } = user || {}
  const [filterBy, setFilterBy] = useState<Seasons | 'ALL'>('ALL')
  const [searchValue, setSearchValue] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [slopes, setSlopes] = useState<Slope[]>([])
  const { refetch: loadUser } = useUser()

  const columns: ColumnProps[] = [
    {
      path: 'name',
      label: t('name')
    },
    {
      content: ({ types }: Slope) => <>{types.join(', ')}</>,
      label: t('season')
    },
    {
      path: 'difficulty',
      label: t('difficulty')
    },

    {
      content: ({ id }: Slope) => (
        <>
          <EditButton to={`edit/${id}`}>
            <IconEdit fill="#2b2b2b" width={20} />
          </EditButton>
        </>
      ),
      label: t('edit')
    }
  ]

  const getAllSlopes = useCallback(async () => {
    setLoading(true)
    const slopes = await getSlopesList()

    const newSlopes = slopes.map(slope => ({
      ...slope,
      types: slope.types.sort()
    }))

    setSlopes(newSlopes)
    setLoading(false)
  }, [])

  useEffect(() => {
    getAllSlopes()
  }, [getAllSlopes, user.resortId])

  useEffect(() => {
    loadUser()
  }, [loadUser])

  const handleBtnFilterClick = (e: React.FormEvent<HTMLInputElement>) => {
    const { name } = e.currentTarget
    setFilterBy(name as Seasons | 'ALL')
  }

  const handleOnChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget
    setSearchValue(value)
  }

  const handleOnSubmit = (e: React.FormEvent) => {
    e.preventDefault()
  }

  const filterData = (data: Slope[], filter: string, key: Key) => {
    if (key !== Key.TYPES) {
      return data.filter(item =>
        item[key].toLowerCase().includes(filter.toLowerCase())
      )
    }
    return data.filter(item => item[key].includes(filter as Seasons))
  }

  const filteredData =
    filterBy !== 'ALL' ? filterData(slopes, filterBy, Key.TYPES) : slopes

  const dataToRender = searchValue.length
    ? filterData(filteredData, searchValue, Key.NAME)
    : filteredData

  return (
    <Container>
      <PageTitle>{t('resortMap')}</PageTitle>

      <HeatmapManager />

      <PageTitle style={{ marginTop: '3rem' }}>{t('slopesOverview')}</PageTitle>
      <ButtonsContainer>
        <form onSubmit={handleOnSubmit}>
          <InputLabel>
            <input
              type="text"
              name="search"
              id="search"
              value={searchValue}
              onChange={handleOnChange}
              placeholder={t('searchSlope')}
            />
          </InputLabel>
          <RadioInputLabel className={filterBy === 'ALL' ? `active` : ''}>
            <input type="radio" name="ALL" onClick={handleBtnFilterClick} />
            {t('all')}
          </RadioInputLabel>
          <RadioInputLabel
            className={filterBy === Seasons.SUMMER ? `active` : ''}
          >
            <input
              type="radio"
              name={Seasons.SUMMER}
              onClick={handleBtnFilterClick}
            />
            {t('summer')}
          </RadioInputLabel>
          <RadioInputLabel
            className={filterBy === Seasons.WINTER ? `active` : ''}
          >
            <input
              type="radio"
              name={Seasons.WINTER}
              onClick={handleBtnFilterClick}
            />
            {t('winter')}
          </RadioInputLabel>
        </form>
        <AddSlopeButton to="create">
          <AddSlopeButtonLabel>{t('addSlope')}</AddSlopeButtonLabel>
          <IconPlusCircle width={20} fill="#2b2b2b" />
        </AddSlopeButton>
      </ButtonsContainer>
      <If condition={loading}>
        <Loading style={{ height: '20vh' }} />
      </If>
      <If condition={!loading && !!slopes.length}>
        <Table data={dataToRender} columns={columns} />
      </If>
    </Container>
  )
}

const mapStateToProps = (state: ApplicationState) => ({
  user: state.auth.data
})

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(AuthActions, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(SlopesPage)
